import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useId, useState } from 'react';
import { Divider } from '../../atoms/Divider/Divider.js';
import { ChevronDown } from '../../icons.js';
import { cn } from '../../lib/cn.js';
import { FOCUS_RING } from '../../lib/focus-ring.js';
/**
 * A titled region a person can fold away.
 *
 * The disclosure is a real `<button>` carrying `aria-expanded`, rather than
 * the `<details>`/`<summary>` this started as. Native `<details>` was correct
 * and invisible: the marker is a UA-styled triangle that reads differently in
 * every browser, and nothing else on the row said it could be clicked. A
 * button gives one chevron, one hover, and one focus ring — the same three the
 * rest of the design system uses — and puts the whole header in the hit area
 * instead of the marker alone.
 *
 * The chevron is one icon rotated, not two icons swapped: the direction is
 * what communicates the state, and rotating it means the change is animated
 * by the same motion token every other control transitions on. Opening and
 * closing animate too — see the content wrapper below for how, and for why
 * the closed state is not `display: none`.
 *
 * State is local and uncontrolled by default. `defaultOpen` decides where a
 * section starts, and which sections a reader has opened is not something the
 * product remembers for them.
 *
 * `open` takes it over for the one case that is not a reading preference: a
 * section that folds because something happened elsewhere on the screen. The
 * uncontrolled state is still kept behind it, so a section handed `open` and
 * then handed `undefined` again carries on from where it was rather than
 * snapping back to `defaultOpen`.
 */
export function CollapsibleSection({ title, meta, defaultOpen = false, open: controlledOpen, onOpenChange, children, headingLevel, divider = true, className, }) {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const open = controlledOpen ?? uncontrolledOpen;
    const contentId = useId();
    function toggle() {
        setUncontrolledOpen(!open);
        onOpenChange?.(!open);
    }
    const Heading = headingLevel ? `h${headingLevel}` : null;
    const trigger = (_jsxs("button", { type: "button", "aria-expanded": open, "aria-controls": contentId, "aria-label": meta ? `${title} · ${meta}` : title, onClick: toggle, className: cn('flex min-h-touch-target-min w-full flex-wrap items-center gap-x-3 gap-y-1', 'rounded-sm px-2 py-2 text-left', 'transition-colors duration-fast hover:bg-surface-secondary', FOCUS_RING), children: [_jsx(ChevronDown, { "aria-hidden": "true", className: cn(
                // No duration class, so this rides the default 180ms token and
                // turns in step with the content opening below it. One gesture.
                'size-icon-sm shrink-0 text-text-secondary transition-transform', !open && '-rotate-90') }), _jsx("span", { className: "text-h4 font-semibold text-text", children: title }), meta ? _jsx("span", { className: "text-body-sm text-text-secondary", children: `· ${meta}` }) : null] }));
    return (_jsxs("div", { className: cn('flex flex-col', className), children: [Heading ? _jsx(Heading, { className: "flex", children: trigger }) : trigger, _jsx("div", { id: contentId, "aria-hidden": !open, inert: !open, className: cn('grid transition-[grid-template-rows]', open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'), children: _jsx("div", { className: "-mx-1 min-h-0 overflow-hidden px-1", children: _jsx("div", { className: "py-4", children: children }) }) }), divider ? _jsx(Divider, {}) : null] }));
}
//# sourceMappingURL=CollapsibleSection.js.map